import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Sparkles, Receipt, Clock, Percent, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeService } from "@/lib/calc";
import { SectionCard, Label2 } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";

function Stat({ icon: Icon, label, value }) {
  return (
    <SectionCard className="!p-5" data-testid={`dashboard-stat-${label}`}>
      <div className="flex items-center gap-2 text-brand-taupe">
        <Icon className="h-4 w-4" />
        <Label2>{label}</Label2>
      </div>
      <div className="mt-3 font-sans text-2xl sm:text-3xl font-semibold text-brand-anthracite tabular-nums">{value}</div>
    </SectionCard>
  );
}

export default function DashboardPage() {
  const { t, user, fmt } = useAuth();
  const navigate = useNavigate();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const { data: costs = [] } = useQuery({ queryKey: ["fixed-costs"], queryFn: () => api.get("/fixed-costs").then((r) => r.data) });

  const fixedTotal = costs.reduce((s, c) => s + Number(c.monthly_amount || 0), 0);
  const computed = services.map((s) => ({ s, c: computeService(s, user) }));
  const avgPrice = computed.length ? computed.reduce((s, { c }) => s + c.finalPrice, 0) / computed.length : 0;
  const avgMargin = computed.length ? computed.reduce((s, { c }) => s + c.margin, 0) / computed.length : 0;

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("dashboard.title")}</h1>
          <p className="mt-1 text-brand-taupe">{t("dashboard.greeting")}</p>
        </div>
        <Button onClick={() => navigate("/services/new")} data-testid="dashboard-cta-new" className="rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 h-12 px-6">
          <Plus className="mr-2 h-5 w-5" /> {t("dashboard.ctaNew")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Sparkles} label={t("dashboard.savedServices")} value={services.length} />
        <Stat icon={Receipt} label={t("dashboard.fixedCostsMonthly")} value={fmt(fixedTotal)} />
        <Stat icon={Percent} label={t("dashboard.avgMargin")} value={`${avgMargin.toFixed(0)}%`} />
        <Stat icon={Clock} label={t("dashboard.hourlyValue")} value={fmt(user.hourly_value)} />
      </div>

      <SectionCard accent>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl font-medium text-brand-anthracite">{t("dashboard.recent")}</h3>
          {services.length > 0 && (
            <button onClick={() => navigate("/services")} className="text-sm text-brand-taupe hover:text-brand-anthracite inline-flex items-center gap-1" data-testid="dashboard-see-all">
              {t("dashboard.seeAll")} <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
        {services.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-brand-taupe">{t("dashboard.empty")}</p>
            <Button onClick={() => navigate("/services/new")} data-testid="dashboard-empty-cta" className="mt-4 rounded-xl bg-brand-anthracite text-brand-ivory">
              {t("dashboard.emptyCta")}
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-brand-line">
            {computed.slice(0, 5).map(({ s, c }) => (
              <button key={s.id} onClick={() => navigate(`/services/${s.id}`)} data-testid={`dashboard-service-${s.id}`}
                className="flex w-full items-center justify-between py-3 text-left hover:opacity-80">
                <div>
                  <div className="font-medium text-brand-anthracite">{s.name}</div>
                  <div className="text-xs text-brand-taupe">{c.effectiveMin.toFixed(0)} {t("common.min")} · {c.margin.toFixed(0)}% {t("services.margin")}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-brand-anthracite tabular-nums">{fmt(c.commercial.nice || c.finalPrice)}</div>
                  {s.current_price ? <div className="text-xs text-brand-taupe">{t("services.current")}: {fmt(s.current_price)}</div> : null}
                </div>
              </button>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
